/**
 * LiensTable - Lien management table with filtering and actions
 */

import { PlusIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { DataTable } from '@/components/common/DataTable/DataTable';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLedgerToast } from '@/hooks/useLedgerToast';
import type { LienStatus, ReadLien } from '@/services/api/ledgerService';
import {
  useLienDestroyMutation,
  useLienListQuery,
  useLienUpdateMutation,
} from '@/services/api/ledgerService';

import { DeleteConfirmDialog } from '../shared/DeleteConfirmDialog';
import { LedgerEmptyState } from '../shared/LedgerEmptyState';
import { createLiensColumns } from './liensColumns';

type StatusFilter = 'all' | LienStatus;

interface LiensTableProps {
  matterId: string;
  onEditLien: (lien: ReadLien) => void;
  onAddLien?: () => void;
}

export const LiensTable = ({
  matterId,
  onEditLien,
  onAddLien,
}: LiensTableProps) => {
  const toast = useLedgerToast();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [lienToDelete, setLienToDelete] = useState<ReadLien | null>(null);

  const {
    data: liens = [],
    isLoading,
    isFetching,
  } = useLienListQuery({
    matterId,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const [updateLien] = useLienUpdateMutation();
  const [deleteLien, { isLoading: isDeleting }] = useLienDestroyMutation();

  const handleResolve = useCallback(
    async (lien: ReadLien) => {
      try {
        await updateLien({
          matterId,
          id: lien.id as string,
          updateLien: { status: 'RESOLVED' },
        }).unwrap();
        toast.showSuccess('update', 'lien');
      } catch {
        toast.showError();
      }
    },
    [matterId, updateLien, toast],
  );

  const handleDelete = useCallback((lien: ReadLien) => {
    setLienToDelete(lien);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!lienToDelete) return;
    try {
      await deleteLien({
        matterId,
        id: lienToDelete.id as string,
      }).unwrap();
      toast.showSuccess('delete', 'lien');
      setLienToDelete(null);
    } catch {
      toast.showError();
    }
  }, [lienToDelete, matterId, deleteLien, toast]);

  const columns = useMemo(
    () =>
      createLiensColumns({
        onEdit: onEditLien,
        onResolve: handleResolve,
        onDelete: handleDelete,
      }),
    [onEditLien, handleResolve, handleDelete],
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between">
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as StatusFilter)}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="RESOLVED">Resolved</SelectItem>
          </SelectContent>
        </Select>
        {onAddLien && (
          <Button onClick={onAddLien} className="bg-blue-600 hover:bg-blue-700">
            <PlusIcon className="mr-2 h-4 w-4" />
            Add Lien
          </Button>
        )}
      </div>

      {/* Table or Empty State */}
      {liens.length === 0 && !isLoading ? (
        <LedgerEmptyState
          title="No liens found"
          description={
            statusFilter !== 'all'
              ? 'Try adjusting your filter'
              : 'Add a lien to track exposure.'
          }
          action={
            statusFilter === 'all' && onAddLien
              ? { label: 'Add lien', onClick: onAddLien }
              : undefined
          }
        />
      ) : (
        <DataTable
          data={liens}
          columns={columns}
          isLoading={isLoading || isFetching}
          getRowKey={(row) => row.id as string}
          density="compact"
          onRowClick={onEditLien}
        />
      )}

      {/* Delete confirmation */}
      <DeleteConfirmDialog
        entityType="lien"
        open={lienToDelete !== null}
        onConfirm={confirmDelete}
        onCancel={() => setLienToDelete(null)}
        isDeleting={isDeleting}
      />
    </div>
  );
};
